import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiGet, apiSend } from './api';
import { pic } from './site';

const SiteContext = createContext(null);

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); } catch { return ''; }
};
const fmtDay = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch { return ''; }
};

// ---- API DTO -> the shapes the existing components already render ----
const mapDest = (d, i = 0) => ({
  id: d.id, slug: d.slug, name: d.name, country: d.country, region: d.region,
  tag: d.tag || 'Beach & Resort', nights: d.nights, from: d.priceFrom, blurb: d.blurb,
  isPublished: d.isPublished, coverImageFileId: d.coverImageFileId, sources: d.sourceReferences || '',
  galleryFileIds: (d.galleryFileIds || '').split('\n').filter(Boolean), galleryUrls: d.galleryUrls || [],
  availability: d.availability || 'Good',
  marginPercent: d.marginPercent ?? null, hotelPriceFrom: d.hotelPriceFrom ?? null,
  img: d.coverImageUrl || pic(`${(d.country || d.name || 'beach').toLowerCase()},beach,travel`, 11 + (d.id % 80 || i)),
  x: 20 + ((i * 13) % 60), y: 30 + ((i * 17) % 40),
});
const mapTour = (t, i = 0) => ({
  id: t.id, name: t.name, place: t.place, dur: t.duration, price: t.price, desc: t.description,
  isActive: t.isActive, imageFileId: t.imageFileId, sources: t.sourceReferences || '',
  img: t.imageUrl || pic(`${(t.place || t.name || 'tour').toLowerCase()},travel`, 41 + (t.id % 60 || i)),
});
const mapPost = (b, i = 0) => ({
  id: b.id, slug: b.slug, title: b.title, cat: b.category, excerpt: b.excerpt, body: b.body,
  mins: b.readMinutes, date: fmtDate(b.publishedAt || b.createdAt), isPublished: b.isPublished,
  sources: b.sourceReferences || '',
  imageFileId: b.imageFileId,
  img: b.imageUrl || pic(`${(b.category || 'travel').toLowerCase()},beach`, 21 + (b.id % 40 || i)),
});
const mapCruise = (c, i = 0) => ({
  id: c.id, name: c.name, area: c.area, nights: c.nights, from: c.priceFrom,
  desc: c.description, body: c.body, ports: c.ports, sources: c.sourceReferences || '',
  isActive: c.isActive, sortOrder: c.sortOrder, imageFileId: c.imageFileId,
  img: c.imageUrl || pic(`cruise,${(c.area || 'ocean').toLowerCase()},ship`, 150 + (c.id % 30 || i)),
});
const mapEvent = (e, i = 0) => ({
  id: e.id, key: e.key, name: e.name, place: e.place, when: e.when, body: e.body,
  isActive: e.isActive, sortOrder: e.sortOrder, imageFileId: e.imageFileId,
  sources: e.sourceReferences || '',
  img: e.imageUrl || pic(`festival,${(e.place || 'event').toLowerCase()}`, 190 + (e.id % 30 || i)),
});
// Country guides keep the API's flat tab fields; pages regroup them as needed.
const mapCountryGuide = (g) => ({ ...g, sources: g.sourceReferences || '' });
const mapTesti = (r) => ({
  id: r.id, name: r.authorName, initial: r.initial, trip: r.tripLabel, text: r.text,
  rating: r.rating, isVisible: r.isVisible,
});
const mapBanner = (b, i = 0) => ({
  id: b.id, title: b.title, sub: b.subtitle, state: b.state, position: b.position,
  imageFileId: b.imageFileId,
  img: b.imageUrl || pic('beach,resort,tropical', 51 + (b.id % 30 || i)),
});

// A mapped destination (or a raw admin form model) -> the API create/update body.
const destBody = (model) => ({
  name: model.name, country: model.country || '—', region: model.region || null,
  tag: model.tag || 'Beach & Resort', nights: Number(model.nights) || 7,
  priceFrom: Number(model.from ?? model.priceFrom) || 0, blurb: model.blurb || null,
  coverImageFileId: model.coverImageFileId || null,
  availability: model.availability || 'Good',
  marginPercent: model.marginPercent ?? null,
  isPublished: model.isPublished ?? true,
  sourceReferences: (model.sources || '').trim() || null,
  galleryFileIds: (model.galleryFileIds || []).join('\n') || null,
});

// Convert a mapped entity back to the API body shape for create/update.
export const toBody = {
  tours: (e) => ({ name: e.name, place: e.place || null, duration: e.dur || null, price: Number(e.price) || 0, description: e.desc || null, imageFileId: e.imageFileId || null, isActive: e.isActive ?? true, sourceReferences: (e.sources || '').trim() || null }),
  posts: (e) => ({ slug: e.slug || null, title: e.title, category: e.cat || null, excerpt: e.excerpt || null, body: e.body || null, imageFileId: e.imageFileId || null, readMinutes: Number(e.mins) || 0, isPublished: e.isPublished ?? false, sourceReferences: (e.sources || '').trim() || null }),
  testimonials: (e) => ({ authorName: e.name, initial: e.initial || null, tripLabel: e.trip || null, text: e.text, rating: Number(e.rating) || 5, isVisible: e.isVisible ?? true }),
  banners: (e) => ({ title: e.title, subtitle: e.sub || null, imageFileId: e.imageFileId || null, state: e.state || 'Draft', position: Number(e.position) || 0 }),
  cruises: (e) => ({ name: e.name, area: e.area || null, nights: e.nights || null, priceFrom: Number(e.from) || 0, description: e.desc || null, body: e.body || null, ports: e.ports || null, imageFileId: e.imageFileId || null, isActive: e.isActive ?? true, sortOrder: Number(e.sortOrder) || 0, sourceReferences: (e.sources || '').trim() || null }),
  countryGuides: (e) => ({ key: e.key, countryName: e.countryName, overviewText: e.overviewText || null, overviewItems: e.overviewItems || null, bestTimeText: e.bestTimeText || null, bestTimeItems: e.bestTimeItems || null, resortsText: e.resortsText || null, resortsItems: e.resortsItems || null, hotelsText: e.hotelsText || null, hotelsItems: e.hotelsItems || null, excursionsText: e.excursionsText || null, excursionsItems: e.excursionsItems || null, currencyText: e.currencyText || null, currencyItems: e.currencyItems || null, weatherText: e.weatherText || null, weatherItems: e.weatherItems || null, visaText: e.visaText || null, visaItems: e.visaItems || null, tipsText: e.tipsText || null, tipsItems: e.tipsItems || null, faqText: e.faqText || null, faqItems: e.faqItems || null, sourceReferences: (e.sources || '').trim() || null, isActive: e.isActive ?? true }),
  events: (e) => ({ key: e.key, name: e.name, place: e.place || null, when: e.when || null, body: e.body || null, imageFileId: e.imageFileId || null, sourceReferences: (e.sources || '').trim() || null, isActive: e.isActive ?? true, sortOrder: Number(e.sortOrder) || 0 }),
};
const mapInquiry = (q) => ({
  id: q.id, ref: `INQ-${String(q.id).padStart(4, '0')}`, name: q.name, email: q.email, phone: q.phone,
  dest: q.destinationText || '—', people: q.email || q.phone || '—', message: q.message,
  date: fmtDay(q.createdAt), total: null, status: q.status,
});

export function SiteProvider({ children }) {
  const [dests, setDests] = useState([]);
  const [tours, setTours] = useState([]);
  const [posts, setPosts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [banners, setBanners] = useState([]);
  const [cruises, setCruises] = useState([]);
  const [events, setEvents] = useState([]);
  const [countryGuides, setCountryGuides] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [agency, setAgency] = useState(null);
  const [stats, setStats] = useState(null);
  const [media, setMedia] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPublic = useCallback(async () => {
    const [d, t, p, r, b, a, c, ev, cg] = await Promise.all([
      apiGet('/api/destinations').catch(() => []),
      apiGet('/api/tours').catch(() => []),
      apiGet('/api/blog-posts').catch(() => []),
      apiGet('/api/testimonials').catch(() => []),
      apiGet('/api/banners').catch(() => []),
      apiGet('/api/agency').catch(() => null),
      apiGet('/api/cruises').catch(() => []),
      apiGet('/api/events').catch(() => []),
      apiGet('/api/country-guides').catch(() => []),
    ]);
    setDests(d.map(mapDest));
    setTours(t.map(mapTour));
    setPosts(p.map(mapPost));
    setTestimonials(r.map(mapTesti));
    setBanners(b.map(mapBanner));
    setCruises(c.map(mapCruise));
    setEvents(ev.map(mapEvent));
    setCountryGuides(cg.map(mapCountryGuide));
    if (a) setAgency(a);
  }, []);

  const updateAgency = async (body) => {
    const updated = await apiSend('PUT', '/api/agency', body);
    setAgency(updated);
    return updated;
  };

  const loadInquiries = useCallback(async () => {
    const q = await apiGet('/api/inquiries').catch(() => null);
    if (Array.isArray(q)) setInquiries(q.map(mapInquiry));
  }, []);

  // ---- Admin-only reads (dashboard + media library) ----
  const loadStats = useCallback(async () => {
    const s = await apiGet('/api/stats');
    setStats(s);
    return s;
  }, []);

  // The signed-in operator, resolved from the Auth session behind /api/admin/me.
  const loadMe = useCallback(async () => {
    const m = await apiGet('/api/admin/me').catch(() => null);
    if (m) setMe(m);
    return m;
  }, []);

  const loadMedia = useCallback(async () => {
    const m = await apiGet('/api/media?take=100');
    setMedia(m?.items ?? []);
    return m;
  }, []);

  /**
   * Removes an image from the library. The API refuses with 409 when a live page still points
   * at it and names where — that message is worth showing verbatim, so it is passed straight up.
   */
  const delMedia = async (fileId) => {
    await apiSend('DELETE', `/api/media/${fileId}`);
    setMedia(ms => ms.filter(m => m.fileId !== fileId));
  };

  useEffect(() => {
    (async () => { await loadPublic(); setLoading(false); })();
  }, [loadPublic]);

  // ---- Destinations CRUD (admin) ----
  const addDest = async (model) => {
    const created = await apiSend('POST', '/api/destinations', destBody(model));
    setDests(ds => [...ds, mapDest(created, ds.length)]);
    return created;
  };
  const updateDest = async (id, model) => {
    const updated = await apiSend('PUT', `/api/destinations/${id}`, destBody(model));
    setDests(ds => ds.map(d => d.id === id ? mapDest(updated) : d));
    return updated;
  };
  // Availability is edited on its own from the Pricing screen — the API takes a full body.
  const setDestAvailability = async (id, availability) => {
    const current = dests.find(d => d.id === id);
    if (!current) return;
    return updateDest(id, { ...current, availability });
  };
  const delDest = async (id) => {
    await apiSend('DELETE', `/api/destinations/${id}`);
    setDests(ds => ds.filter(d => d.id !== id));
  };

  // ---- Generic CRUD for Tours / Blog / Testimonials / Banners ----
  const ENTITY = {
    tours: { path: '/api/tours', map: mapTour, set: setTours },
    posts: { path: '/api/blog-posts', map: mapPost, set: setPosts },
    testimonials: { path: '/api/testimonials', map: mapTesti, set: setTestimonials },
    banners: { path: '/api/banners', map: mapBanner, set: setBanners },
    cruises: { path: '/api/cruises', map: mapCruise, set: setCruises },
    events: { path: '/api/events', map: mapEvent, set: setEvents },
    countryGuides: { path: '/api/country-guides', map: mapCountryGuide, set: setCountryGuides },
  };
  const saveEntity = async (kind, id, body) => {
    const e = ENTITY[kind];
    if (id) {
      const u = await apiSend('PUT', `${e.path}/${id}`, body);
      e.set(xs => xs.map(x => x.id === id ? e.map(u) : x));
      return u;
    }
    const c = await apiSend('POST', e.path, body);
    e.set(xs => [...xs, e.map(c, xs.length)]);
    return c;
  };
  const deleteEntity = async (kind, id) => {
    const e = ENTITY[kind];
    await apiSend('DELETE', `${e.path}/${id}`);
    e.set(xs => xs.filter(x => x.id !== id));
  };
  // Toggle a single boolean/enum field on a mapped entity (rebuilds the full body).
  const toggleField = async (kind, entity, patch) => {
    const body = { ...toBody[kind](entity), ...patch };
    return saveEntity(kind, entity.id, body);
  };

  // ---- Inquiries ----
  const delInquiry = async (id) => {
    await apiSend('DELETE', `/api/inquiries/${id}`);
    setInquiries(qs => qs.filter(q => q.id !== id));
  };
  const createInquiry = async (payload) => {
    const created = await apiSend('POST', '/api/inquiries', payload);
    setInquiries(qs => [mapInquiry(created), ...qs]);
    return created;
  };
  const cycleInquiry = async (id) => {
    const order = ['New', 'Pending', 'Confirmed'];
    const cur = inquiries.find(q => q.id === id);
    if (!cur) return;
    const next = order[(order.indexOf(cur.status) + 1) % 3];
    const updated = await apiSend('PUT', `/api/inquiries/${id}/status`, { status: next });
    setInquiries(qs => qs.map(q => q.id === id ? mapInquiry(updated) : q));
  };

  const digits = (s) => (s || '').replace(/[^0-9]/g, '');
  const contact = {
    brand: agency?.brandName || 'HolaCaribe',
    phone: agency?.phone || '+1 809 555 0142',
    waLink: 'https://wa.me/' + (digits(agency?.whatsApp) || '18095550142'),
    telLink: 'tel:+' + (digits(agency?.phone) || '18095550142'),
    igHandle: (agency?.instagram || '@holacaribe.travel').replace(/^@/, ''),
    igLink: 'https://instagram.com/' + (agency?.instagram || 'holacaribe.travel').replace(/^@/, ''),
    email: agency?.supportEmail || 'info@holacaribetravel.com',
  };

  return (
    <SiteContext.Provider value={{
      loading, dests, tours, posts, testimonials, banners, cruises, events, countryGuides,
      inquiries, bookings: inquiries,           // admin screens historically call it "bookings"
      agency, contact, updateAgency,
      stats, loadStats, media, setMedia, loadMedia, delMedia, me, loadMe,
      addDest, updateDest, delDest, setDestAvailability,
      saveEntity, deleteEntity, toggleField,
      createInquiry, cycleInquiry, cycleBooking: cycleInquiry, delInquiry,
      loadInquiries, reload: loadPublic,
    }}>
      {children}
    </SiteContext.Provider>
  );
}

export const useSite = () => useContext(SiteContext);
